// The console tab's side of the phone. Connects to the SharedWorker that holds the browser's one phone (hub.ts), or
// runs the hub in this page where there is no SharedWorker. Makes the phone's server calls with this tab's sign-in,
// forwards 'softphone' stream messages to it, and turns its state into the console's (softphone.ts).
//
// Nothing secret is stored in the browser: no RingCentral token reaches it at all (the server keeps it), and the only
// thing kept in localStorage is whether the panel is expanded.

import { api, errorText } from '../api.ts';
import { IDLE, type ControlAction, type ControlArgs, type HubMessage, type PhoneState, type PortLike, type TabMessage } from './engine.ts';
import { makeEngine } from './engines.ts';
import { PhoneHub } from './hub.ts';
import { MediaHost } from './media-host.ts';
import { rcSignIn } from './rc-signin.ts';
import { callEvents, softphoneState, type Softphone, type SoftphoneEvent, type SoftphoneState } from './softphone.ts';

const OPEN_KEY = 'eyeqsoc.phoneOpen';
const CMD_TIMEOUT_MS = 30_000;

function readOpen(): boolean {
  try {
    return localStorage.getItem(OPEN_KEY) !== '0';
  } catch {
    return true;
  }
}

/** A port to the phone: the SharedWorker's, or one end of a channel to a hub in this page. */
function connect(): PortLike {
  if (typeof SharedWorker !== 'undefined') {
    try {
      const w = new SharedWorker(new URL('./phone-worker.ts', import.meta.url), { type: 'module', name: 'eyeq-soc-phone' });
      return w.port as unknown as PortLike;
    } catch (err) {
      console.warn('SharedWorker unavailable; the phone runs in this tab only', err);
    }
  }
  const ch = new MessageChannel();
  new PhoneHub({ makeEngine }).attach(ch.port2 as unknown as PortLike);
  return ch.port1 as unknown as PortLike;
}

export class PhoneClient implements Softphone {
  readonly label: string;
  private readonly tabId = crypto.randomUUID();
  private port: PortLike | null = null;
  private phone: PhoneState = IDLE;
  private open = readOpen();
  private state: SoftphoneState;
  private subs = new Set<() => void>();
  private listeners = new Set<(e: SoftphoneEvent) => void>();
  private cmds = new Map<string, { resolve: (v: unknown) => void; reject: (e: Error) => void; timer: ReturnType<typeof setTimeout> }>();
  private seq = 0;
  /** RingCentral: the calls whose audio this tab plays. */
  private media: MediaHost | null = null;

  constructor(readonly engine: 'simulator' | 'ringcentral') {
    this.label = engine === 'simulator' ? 'Simulated phone' : 'RingCentral';
    this.state = softphoneState(IDLE, this.open);
  }

  getState = (): SoftphoneState => this.state;

  subscribe = (cb: () => void): (() => void) => {
    this.subs.add(cb);
    return () => this.subs.delete(cb);
  };

  on(listener: (e: SoftphoneEvent) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(e: SoftphoneEvent) {
    for (const l of this.listeners) {
      try {
        l(e);
      } catch (err) {
        console.error('softphone listener failed', err);
      }
    }
  }

  private setPhone(next: PhoneState) {
    const before = this.phone;
    this.phone = next;
    this.state = softphoneState(next, this.open);
    for (const cb of this.subs) cb();
    if (before.registration !== next.registration && (next.registration === 'registered' || next.registration === 'needs_sign_in')) this.emit({ type: 'login', loggedIn: next.registration === 'registered' });
    for (const e of callEvents(before.calls, next.calls)) this.emit(e);
  }

  private post(m: TabMessage) {
    this.port?.postMessage(m);
  }

  load(): void {
    if (this.port) return;
    this.port = connect();
    this.port.onmessage = (e) => void this.onMessage(e.data as HubMessage);
    this.port.start?.();
    this.post({ t: 'hello', tabId: this.tabId, engine: this.engine, visible: document.visibilityState === 'visible' });
    setInterval(() => this.post({ t: 'ping', tabId: this.tabId }), 10_000);
    const active = () => {
      if (document.visibilityState === 'visible') this.activate();
    };
    document.addEventListener('visibilitychange', active);
    window.addEventListener('focus', active);
    window.addEventListener('pagehide', () => this.post({ t: 'bye', tabId: this.tabId }));
    window.addEventListener('online', () => this.reconnect());
  }

  activate(): void {
    this.post({ t: 'active', tabId: this.tabId });
  }

  private async onMessage(m: HubMessage) {
    if (m.t === 'state') return this.setPhone(m.state);
    if (m.t === 'result') {
      const c = this.cmds.get(m.id);
      if (!c) return;
      clearTimeout(c.timer);
      this.cmds.delete(m.id);
      return m.ok ? c.resolve(m.data) : c.reject(new Error(m.error ?? 'the phone could not do that'));
    }
    if (m.t === 'media') {
      try {
        this.media ??= new MediaHost();
        const data = await this.media.handle(m.op, m.args);
        this.post({ t: 'media_result', id: m.id, ok: true, data });
      } catch (err) {
        this.post({ t: 'media_result', id: m.id, ok: false, error: errorText(err) });
      }
      return;
    }
    if (m.t === 'http') {
      try {
        const data = await api.softphoneHttp(m.method, m.path, m.body);
        this.post({ t: 'http_result', id: m.id, ok: true, data });
      } catch (err) {
        this.post({ t: 'http_result', id: m.id, ok: false, error: errorText(err), status: (err as { status?: number }).status });
      }
    }
  }

  private cmd<T>(op: 'start' | 'stop' | 'recover' | 'dial' | 'control', args?: Record<string, unknown>): Promise<T> {
    this.load();
    const id = `${this.tabId}:${++this.seq}`;
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.cmds.delete(id);
        reject(new Error('the phone did not answer'));
      }, CMD_TIMEOUT_MS);
      this.cmds.set(id, { resolve: resolve as (v: unknown) => void, reject, timer });
      this.post({ t: 'cmd', id, op, args });
    });
  }

  async dial(e164: string, callerId: string | null = null, o: { openPanel?: boolean } = {}): Promise<void> {
    if (o.openPanel !== false) this.setOpen(true);
    await this.cmd('dial', { to: e164, callerId });
  }

  async hangup(telephonySessionId: string | null): Promise<void> {
    const live = this.phone.calls.filter((c) => c.state !== 'ended');
    const call = telephonySessionId ? live.find((c) => c.telephonySessionId === telephonySessionId) : live.length === 1 ? live[0] : undefined;
    if (!call) throw new Error('that call is not on this phone');
    await this.control(call.id, 'hangup');
  }

  control(callId: string, action: ControlAction, args: ControlArgs = {}): Promise<{ consultCallId?: string }> {
    return this.cmd('control', { callId, action, args });
  }

  reconnect(): void {
    void this.cmd('recover').catch(() => undefined);
  }

  serverMessage(data: unknown): void {
    this.post({ t: 'server', data });
  }

  /**
   * RingCentral sign-in: the server starts it (PKCE; the verifier stays there), RingCentral's page opens in a pop-up,
   * /rc-callback.html hands the code back to this window, and the server exchanges it. Then the phone registers.
   */
  async signIn(): Promise<void> {
    if (this.engine !== 'ringcentral') return;
    await rcSignIn(() => api.softphoneLogin());
    await this.cmd('start', { engine: 'ringcentral' }).catch(() => undefined);
  }

  /** The agent sat down at or left a workstation: the phone registers again, as the workstation's (or their own). */
  restart(): void {
    void this.cmd('stop')
      .catch(() => undefined)
      .then(() => this.cmd('start', { engine: this.engine }))
      .catch(() => undefined);
  }

  async signOut(): Promise<void> {
    if (this.engine !== 'ringcentral') return;
    await this.cmd('stop').catch(() => undefined);
    await api.softphoneLogout();
    await this.cmd('start', { engine: 'ringcentral' }).catch(() => undefined);
  }

  setOpen(open: boolean): void {
    try {
      localStorage.setItem(OPEN_KEY, open ? '1' : '0');
    } catch {
      /* ignore */
    }
    this.open = open;
    this.state = softphoneState(this.phone, open);
    for (const cb of this.subs) cb();
  }
}
